'use client';

import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LateCancelPenaltyChargeStatus,
  PrestationPaymentType,
  PriceType,
  ProPaymentStatus,
  ReservationStatus,
  RideTripLeg,
  formatDurationFromMinutes,
  groupRideLegsByVehicle,
  rideLuggageDisplayParts,
  translate,
} from '@cfixe-monorepo/shared';
import type {
  BookableSlotObject,
  BookingConfirmationResponseObject,
  RideLuggageDisplayPart,
  CancelConfirmedBookingResponseObject,
} from '@cfixe-monorepo/shared';
import type { IconType } from 'react-icons';
import {
  FiAlertCircle,
  FiCalendar,
  FiClock,
  FiFileText,
  FiMail,
  FiMapPin,
  FiPhone,
  FiTruck,
  FiUser,
  FiX,
} from 'react-icons/fi';
import {
  PublicApiError,
  cancelConfirmedBooking,
  fetchRescheduleBookingSlots,
  getBookingConfirmation,
  rescheduleConfirmedBooking,
} from '../../../website/api/professional-api';
import { getRequiredCaptchaToken, type CapInstance } from '../../../website/functions/captcha';
import { SlotWeekPicker } from '../shared/SlotWeekPicker';
import { getFirstAvailableSlotDate } from '../shared/slot-picker-utils';
import { slotWindowEnd, slotWindowStart, toISODate } from '../../utils/date-helpers';
import { formatFlexibleAmount, hasNonZeroFormattedAmount } from '../../utils/payment-amounts';
import { discountLineLabel, voucherLineLabel } from '../../utils/amount-lines';
import { bookedLineLabel } from '../../utils/recap-lines';
import { TeamMemberAvatar } from '../../../shared/TeamMemberAvatar';
import '../../styles/booking-manage-view.scss';

interface BookingManageViewProps {
  uuid: string;
  className?: string;
  showIntro?: boolean;
  onBookingLoaded?: (booking: BookingConfirmationResponseObject) => void;
  onCancelSuccess?: (booking: BookingConfirmationResponseObject) => void;
  onRescheduleSuccess?: (booking: BookingConfirmationResponseObject) => void;
}

type ActiveManageModal = 'cancel' | 'reschedule-picker' | 'reschedule-confirm' | null;

interface DetailItemProps {
  icon: IconType;
  label: string;
  children: ReactNode;
}

type BookingRefundDetails = {
  paymentRefundAmount?: string;
  voucherRefund?: {
    code: string;
    amount: string;
  };
  /**
   * Frais d'annulation tardive d'un paiement à collecte différée (CARD_SAVE / BANK_IMPRINT)
   * pas encore encaissé. Montant formaté backend, à afficher tel quel.
   */
  deferredPenalty?: {
    amount: string;
    isBankImprint: boolean;
    /** Deadline dépassée : le modal d'annulation doit avertir des frais. */
    isPastDeadline: boolean;
    /** CARD_SAVE hors délai : le prélèvement sera tenté automatiquement à la confirmation. */
    willBeCharged: boolean;
  };
  isDeferredCollection: boolean;
};

function t(key: string): string {
  return translate('fr-FR', 'BOOKING_PUBLIC_PAGE', key) ?? key;
}

/** Libellés des lignes du bloc `amounts`, les mêmes que dans le récap du tunnel. */
function codesLabel(key: string): string {
  return translate('fr-FR', 'BOOKING_TUNNEL_CODES', key) ?? key;
}

function toSearchStartIso(dateValue: string): string | undefined {
  if (!dateValue) return undefined;
  return new Date(`${dateValue}T00:00:00`).toISOString();
}

function getCustomerName(booking: BookingConfirmationResponseObject): string {
  return [booking.customer_firstname, booking.customer_lastname].filter(Boolean).join(' ');
}

type BookingDisplayMember = {
  key: string;
  name: string;
  pictureUrl?: string;
};

type MemberImagePreview = {
  src: string;
  alt: string;
};

function buildMapSearchUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

function formatMemberName(firstname?: string | null, lastname?: string | null): string | undefined {
  return [firstname, lastname].filter(Boolean).join(' ') || undefined;
}

function getMemberPictureUrl(memberPicture?: Array<{ preview_url?: string | null }> | null): string | undefined {
  return memberPicture?.[0]?.preview_url ?? undefined;
}

function getSlotMembers(slot: NonNullable<BookingConfirmationResponseObject['slots']>[number]): BookingDisplayMember[] {
  const members = slot.team_members ?? [];
  if (members.length > 0) {
    return members.flatMap((member): BookingDisplayMember[] => {
      const name = formatMemberName(member.firstname, member.lastname);
      if (!name) return [];
      return [
        {
          key: `member-${member.team_member_id}`,
          name,
          pictureUrl: getMemberPictureUrl(member.picture),
        },
      ];
    });
  }

  const legacyName = formatMemberName(slot.team_member_firstname, slot.team_member_lastname);
  if (!legacyName) return [];

  return [
    {
      key: `legacy-${legacyName}`,
      name: legacyName,
      pictureUrl: getMemberPictureUrl(slot.team_member_picture),
    },
  ];
}

function getServiceSummaries(booking: BookingConfirmationResponseObject): string[] {
  // `slots` peut être null/absent : pré-résa expirée/annulée (transformToSchema vide → null).
  return (booking.slots ?? []).flatMap((slot): string[] =>
    (slot.prestations ?? []).map((prestation): string => {
      /* Multiplicateur de la ligne (même libellé que le récap de la confirmation). `party_size` (nb de personnes)
         prime sur `quantity` et n'est envoyé qu'au-delà d'une unité ; sans lui, une réservation pour 4 s'affichait
         « x1 » en face d'un total quadruplé. `x1` n'apporte rien et disparaît. */
      const base = bookedLineLabel(prestation, t('SERVICE_PARTY_SIZE_SUFFIX'));
      const supplements = prestation.supplements ?? [];
      if (supplements.length === 0) return base;
      // Quantité renseignée par le backend au-delà d'un seul exemplaire du supplément.
      const supplementsLabel = supplements
        .map((s): string => (s.quantity !== undefined && s.quantity > 1 ? `${s.name} × ${s.quantity}` : s.name))
        .join(', ');
      return `${base} (+ ${supplementsLabel})`;
    }),
  );
}

type BookingSlot = NonNullable<BookingConfirmationResponseObject['slots']>[number];
type BookingRideLeg = NonNullable<BookingSlot['ride_details']>[number];

/** Un véhicule de la réservation, tel qu'affiché dans le bloc « Vos véhicules (N) ». */
type RideDisplayVehicle = {
  /** Position du véhicule dans la réservation (1..N). */
  vehicleIndex: number;
  /** Classe réservée, ou classe PRÉVUE quand aucune unité n'a encore été affectée. */
  vehicleName?: string;
  /** Aucune unité affectée (surbooking) : la classe affichée est celle qui était demandée. */
  isToAssign: boolean;
  passengerCount?: number;
  luggageCount?: number;
  smallLuggageCount?: number | null;
  largeLuggageCount?: number | null;
  driverName?: string;
  /** Prix de CE véhicule, déjà formaté par le backend (règle « Price Handling »). */
  price?: string;
};

/**
 * Véhicules de la réservation, un par `vehicle_index`.
 *
 * L'appariement aller/retour passe par `groupRideLegsByVehicle` (source unique partagée avec le
 * backend et l'agenda pro) : sans lui, « l'autre créneau de la réservation » serait pris pour le
 * retour, ce qui mélangerait les adresses de deux véhicules faisant chacun un aller simple.
 * Une course mono-véhicule renvoie une seule entrée — le bloc reste alors masqué.
 */
function getRideVehicles(booking: BookingConfirmationResponseObject): RideDisplayVehicle[] {
  type RideLegWithSlot = { trip_leg?: string; vehicle_index?: number; leg: BookingRideLeg; slot: BookingSlot };
  const legRows: RideLegWithSlot[] = (booking.slots ?? []).flatMap((slot): RideLegWithSlot[] =>
    (slot.ride_details ?? []).map(
      (leg): RideLegWithSlot => ({ trip_leg: leg.trip_leg, vehicle_index: leg.vehicle_index, leg, slot }),
    ),
  );

  return groupRideLegsByVehicle(legRows).map((group): RideDisplayVehicle => {
    // L'ALLER porte la part du groupe et le prix du véhicule ; à défaut, le premier leg.
    const outbound = group.legs.find((row): boolean => row.leg.trip_leg === RideTripLeg.OUTBOUND) ?? group.legs[0];
    const priced = group.legs.find((row): boolean => Boolean(row.leg.vehicle_price_string)) ?? outbound;
    return {
      vehicleIndex: group.vehicle_index,
      vehicleName: outbound.slot.vehicle_name ?? outbound.leg.requested_vehicle_name,
      isToAssign: !outbound.slot.vehicle_name && Boolean(outbound.leg.requested_vehicle_name),
      passengerCount: outbound.leg.passenger_count,
      luggageCount: outbound.leg.luggage_count,
      smallLuggageCount: outbound.leg.small_luggage_count,
      largeLuggageCount: outbound.leg.large_luggage_count,
      driverName: getSlotMembers(outbound.slot)[0]?.name,
      price: priced.leg.vehicle_price_string,
    };
  });
}

/** Nombre total de passagers du groupe (somme des parts de chaque véhicule). */
function getRideTotalPassengers(vehicles: RideDisplayVehicle[]): number | undefined {
  const counts = vehicles
    .map((vehicle): number | undefined => vehicle.passengerCount)
    .filter((count): count is number => count != null);
  return counts.length > 0 ? counts.reduce((sum: number, count): number => sum + count, 0) : undefined;
}

const RIDE_LUGGAGE_LINE_KEYS: Record<RideLuggageDisplayPart['size'], string> = {
  total: 'RIDE_LUGGAGE',
  small: 'RIDE_SMALL_LUGGAGE',
  large: 'RIDE_LARGE_LUGGAGE',
};

/** Règle d'affichage partagée (`rideLuggageDisplayParts`) : tailles non nulles, sinon « Bagages : 0 ». */
function formatRideLuggageLines(total?: number, small?: number | null, large?: number | null): string[] {
  return rideLuggageDisplayParts({
    luggage_count: total,
    small_luggage_count: small,
    large_luggage_count: large,
  }).map((part): string => `${t(RIDE_LUGGAGE_LINE_KEYS[part.size])} : ${part.count}`);
}

/** « Véhicule 2 : Van 7 places · Passagers : 6 · Petits bagages : 1 · Grands bagages : 2 · Chauffeur : Paul D. · 120,00 € ». */
function formatRideVehicleLine(vehicle: RideDisplayVehicle): string {
  const segments: string[] = [];
  if (vehicle.isToAssign) {
    segments.push(
      vehicle.vehicleName ? `${vehicle.vehicleName} (${t('RIDE_VEHICLE_TO_ASSIGN')})` : t('RIDE_VEHICLE_TO_ASSIGN'),
    );
  } else if (vehicle.vehicleName) {
    segments.push(vehicle.vehicleName);
  }
  if (vehicle.passengerCount != null) segments.push(`${t('RIDE_PASSENGERS')} : ${vehicle.passengerCount}`);
  segments.push(...formatRideLuggageLines(vehicle.luggageCount, vehicle.smallLuggageCount, vehicle.largeLuggageCount));
  if (vehicle.driverName) segments.push(`${t('RIDE_DRIVER')} : ${vehicle.driverName}`);
  if (vehicle.price) segments.push(vehicle.price);
  const title = t('RIDE_VEHICLE_ITEM').replace('{index}', String(vehicle.vehicleIndex));
  return segments.length > 0 ? `${title} : ${segments.join(' · ')}` : title;
}

function getBookingMembers(booking: BookingConfirmationResponseObject): BookingDisplayMember[] {
  const byKey = new Map<string, BookingDisplayMember>();
  for (const slot of booking.slots ?? []) {
    const slotMembers = getSlotMembers(slot);
    for (const member of slotMembers) {
      if (!byKey.has(member.key)) {
        byKey.set(member.key, member);
      }
    }
  }
  return [...byKey.values()];
}

function getSlotDateTime(slot: NonNullable<BookingConfirmationResponseObject['slots']>[number]): string {
  return [slot.start_formatted, slot.end_formatted].filter(Boolean).join(' - ');
}

function getBookableSlotLabel(slot: BookableSlotObject): string {
  const startLabel = slot.slot_start_string ?? slot.slot_start;
  const endLabel = slot.slot_end_time_string ?? slot.slot_end;
  return [startLabel, endLabel].filter(Boolean).join(' - ');
}

function DetailItem({ icon: Icon, label, children }: DetailItemProps): React.JSX.Element {
  return (
    <div className="manage-booking-page__detail-item">
      <Icon className="manage-booking-page__detail-icon" aria-hidden="true" />
      <div>
        <span>{label}</span>
        <strong>{children}</strong>
      </div>
    </div>
  );
}

function getErrorMessage(error: unknown): string {
  if (error instanceof PublicApiError) return error.message;
  if (error instanceof Error) return error.message;
  return t('ERROR_LOADING');
}

function isBeforeCancellationDeadline(booking: BookingConfirmationResponseObject): boolean {
  const explicitValue = booking.booking_cancellation_refund_info?.is_before_cancellation_deadline;
  if (explicitValue !== undefined) return explicitValue;
  if (!booking.cancellation_deadline) return false;
  return new Date(booking.cancellation_deadline).getTime() >= Date.now();
}

function isDeferredCollection(booking: BookingConfirmationResponseObject): boolean {
  return (
    booking.payment_type === PrestationPaymentType.CARD_SAVE ||
    booking.payment_type === PrestationPaymentType.BANK_IMPRINT
  );
}

function getBookingRefundDetails(booking: BookingConfirmationResponseObject): BookingRefundDetails {
  const deferred = isDeferredCollection(booking);
  const hasCollectedOnlinePayment =
    booking.payment_status === ProPaymentStatus.PAYED || booking.payment_status === ProPaymentStatus.PARTIALLY_REFUNDED;
  // Paiement différé jamais encaissé : current_refund_amount est un « remboursable théorique »
  // (rien n'a été débité) — ne pas l'afficher comme un remboursement.
  const paymentRefundAmount =
    deferred && !hasCollectedOnlinePayment
      ? undefined
      : (booking.booking_cancellation_refund_info?.current_refund_amount ??
        booking.payment_infos.possible_refund_out_time);
  const hasPaymentRefundAmount = hasNonZeroFormattedAmount(paymentRefundAmount);
  const voucherRefundInfo = booking.voucher_refund_info;
  const beforeDeadline = isBeforeCancellationDeadline(booking);
  // Seul le snapshot fait foi : c'est le montant que le backend débitera réellement. Le fee « live »
  // (payment_infos.cancellation_fee_out_time, recalculé depuis le catalogue courant) peut diverger
  // du snapshot et promettrait un prélèvement qui n'aura jamais lieu.
  const penaltyAmount = booking.booking_cancellation_refund_info?.late_cancel_penalty_amount;
  const isBankImprint = booking.payment_type === PrestationPaymentType.BANK_IMPRINT;
  // AUTHORIZED = carte réellement enregistrée / empreinte posée. Un SetupIntent jamais complété
  // (WAITING_PAYMENT) n'a rien de prélevable : ne pas promettre de débit au client.
  const hasChargeableGuarantee = booking.payment_status === ProPaymentStatus.AUTHORIZED;
  const deferredPenalty =
    deferred && hasChargeableGuarantee && penaltyAmount !== undefined && hasNonZeroFormattedAmount(penaltyAmount)
      ? {
          amount: penaltyAmount,
          isBankImprint,
          isPastDeadline: !beforeDeadline,
          willBeCharged: !beforeDeadline && !isBankImprint,
        }
      : undefined;
  // `is_refundable` est autoritaire : le backend y intègre déjà la deadline ET la politique
  // de restitution du pro (gift_card_settings snapshotée à la résa).
  const shouldUseVoucherRefundInfo =
    voucherRefundInfo != null && voucherRefundInfo.is_refundable && hasNonZeroFormattedAmount(voucherRefundInfo.amount);
  const shouldUseLegacyVoucherRefund =
    !voucherRefundInfo &&
    beforeDeadline &&
    hasNonZeroFormattedAmount(booking.voucher_discount) &&
    Boolean(booking.voucher_code);

  return {
    paymentRefundAmount: hasPaymentRefundAmount ? paymentRefundAmount : undefined,
    voucherRefund:
      shouldUseVoucherRefundInfo && voucherRefundInfo
        ? {
            code: voucherRefundInfo.code,
            amount: voucherRefundInfo.amount,
          }
        : shouldUseLegacyVoucherRefund && booking.voucher_code && booking.voucher_discount
          ? {
              code: booking.voucher_code,
              amount: booking.voucher_discount,
            }
          : undefined,
    deferredPenalty,
    isDeferredCollection: deferred,
  };
}

export function BookingManageView({
  uuid,
  className,
  showIntro = true,
  onBookingLoaded,
  onCancelSuccess,
  onRescheduleSuccess,
}: BookingManageViewProps): React.JSX.Element {
  const capRef = useRef<CapInstance | null>(null);
  const [booking, setBooking] = useState<BookingConfirmationResponseObject | null>(null);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [isLoadingBooking, setIsLoadingBooking] = useState(true);
  const [cancellationReason, setCancellationReason] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [cancelResult, setCancelResult] = useState<CancelConfirmedBookingResponseObject | null>(null);
  const [activeModal, setActiveModal] = useState<ActiveManageModal>(null);
  /* Fenêtres de 7 jours qui partent d'aujourd'hui, comme dans le tunnel (bug 34). */
  const [today] = useState((): string => toISODate(new Date()));
  const [slotWeekStart, setSlotWeekStart] = useState(today);
  const [selectedSlotDate, setSelectedSlotDate] = useState<string | undefined>(undefined);
  const [slots, setSlots] = useState<BookableSlotObject[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<BookableSlotObject | null>(null);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [memberImagePreview, setMemberImagePreview] = useState<MemberImagePreview | null>(null);

  const rootClassName = useMemo(
    (): string => ['booking-manage-view', className].filter(Boolean).join(' '),
    [className],
  );

  const loadBooking = useCallback(async (): Promise<BookingConfirmationResponseObject | null> => {
    setIsLoadingBooking(true);
    setBookingError(null);
    try {
      const response = await getBookingConfirmation(uuid, { next: { revalidate: 0 } });
      setBooking(response);
      onBookingLoaded?.(response);
      return response;
    } catch (error) {
      setBookingError(getErrorMessage(error));
      return null;
    } finally {
      setIsLoadingBooking(false);
    }
  }, [onBookingLoaded, uuid]);

  useEffect((): void => {
    void loadBooking();
  }, [loadBooking]);

  const handleCancel = async (): Promise<void> => {
    setIsCancelling(true);
    setActiveModal(null);
    setActionError(null);
    setActionMessage(null);
    setCancelResult(null);
    try {
      const token = await getRequiredCaptchaToken(capRef);
      const result = await cancelConfirmedBooking(
        uuid,
        {
          cancellation_reason: cancellationReason.trim() || undefined,
        },
        token,
      );
      setCancelResult(result);
      setCancellationReason('');
      setActionMessage(
        result.late_cancel_penalty_charge_status === LateCancelPenaltyChargeStatus.CHARGED &&
          result.late_cancel_penalty_amount
          ? t('CANCEL_SUCCESS_PENALTY_CHARGED').replace('{amount}', result.late_cancel_penalty_amount)
          : t('CANCEL_SUCCESS'),
      );
      const refreshedBooking = await loadBooking();
      if (refreshedBooking) onCancelSuccess?.(refreshedBooking);
    } catch (error) {
      setActionError(getErrorMessage(error));
    } finally {
      setIsCancelling(false);
    }
  };

  const handleLoadSlots = async (weekStart = slotWeekStart, isRetarget = false): Promise<void> => {
    setIsLoadingSlots(true);
    setActionError(null);
    setActionMessage(null);
    setSelectedSlot(null);
    setSlots([]);
    setSelectedSlotDate(undefined);
    try {
      const token = await getRequiredCaptchaToken(capRef);
      const response = await fetchRescheduleBookingSlots(uuid, { start_date: toSearchStartIso(weekStart) }, token);
      setSlots(response);
      const firstDate = getFirstAvailableSlotDate(response, weekStart);
      setSelectedSlotDate(firstDate);
      /* The backend clamps the search to now + the pro's minimum booking delay,
         so slots can land beyond the requested week: move the grid onto them AND
         refetch that full week (audit P2 #8) — the week-N response only carries the
         tail of the 7-day window, the rest of the target week would show as closed.
         Single rebound: the target is monotonic, one refetch converges (loop belt). */
      if (firstDate !== undefined && (firstDate < weekStart || firstDate > slotWindowEnd(weekStart))) {
        const firstDateWeek = slotWindowStart(firstDate, today);
        setSlotWeekStart(firstDateWeek);
        // AWAIT obligatoire : lancé en `void`, le rebond vide la liste puis rend la main, et
        // le `finally` ci-dessous éteindrait le spinner sur une liste vide — le client lirait
        // « aucune disponibilité » et pourrait changer de semaine pendant que le rebond est
        // encore en vol, dont la réponse tardive écraserait alors la mauvaise semaine.
        if (!isRetarget) await handleLoadSlots(firstDateWeek, true);
      }
    } catch (error) {
      setActionError(getErrorMessage(error));
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleOpenReschedulePanel = (): void => {
    setActiveModal('reschedule-picker');
    void handleLoadSlots(slotWeekStart);
  };

  const handleRescheduleWeekChange = (weekStart: string): void => {
    setSlotWeekStart(weekStart);
    void handleLoadSlots(weekStart);
  };

  const handleReschedule = async (): Promise<void> => {
    if (!selectedSlot?.slots?.length) return;
    setIsRescheduling(true);
    setActiveModal(null);
    setActionError(null);
    setActionMessage(null);
    try {
      const token = await getRequiredCaptchaToken(capRef);
      const response = await rescheduleConfirmedBooking(
        uuid,
        {
          slots: selectedSlot.slots,
          reschedule_reason: rescheduleReason.trim() || undefined,
        },
        token,
      );
      setBooking(response);
      setSelectedSlot(null);
      setSlots([]);
      setRescheduleReason('');
      // Pro en validation manuelle : le déplacement n'est qu'une demande tant qu'il n'a pas confirmé.
      setActionMessage(
        t(
          response.reservation_status === ReservationStatus.WAITING_CONFIRMATION
            ? 'RESCHEDULE_REQUEST_SUCCESS'
            : 'RESCHEDULE_SUCCESS',
        ),
      );
      onRescheduleSuccess?.(response);
    } catch (error) {
      setActionError(getErrorMessage(error));
    } finally {
      setIsRescheduling(false);
    }
  };

  const refundDetails = booking ? getBookingRefundDetails(booking) : undefined;
  const hasRefundDetails = Boolean(refundDetails?.paymentRefundAmount || refundDetails?.voucherRefund);
  // `slots` peut être null/absent : pré-résa expirée/annulée (transformToSchema vide → null).
  const bookingSlots = booking?.slots ?? [];
  const primarySlot = bookingSlots[0];
  const serviceSummaries = booking ? getServiceSummaries(booking) : [];
  const memberDetails = booking ? getBookingMembers(booking) : [];
  const placeAddress = primarySlot
    ? (primarySlot.pro_place_address_formatted ?? primarySlot.customer_address_formatted)
    : undefined;
  // Course VTC : leg aller (adresses/distance/durée) + véhicule — remplace lieu & prestations.
  const isRide = booking?.is_ride === true;
  const rideSlot = isRide
    ? (bookingSlots.find((slot): boolean =>
        (slot.ride_details ?? []).some((leg): boolean => leg.trip_leg === RideTripLeg.OUTBOUND),
      ) ?? primarySlot)
    : undefined;
  const rideLeg = rideSlot
    ? ((rideSlot.ride_details ?? []).find((leg): boolean => leg.trip_leg === RideTripLeg.OUTBOUND) ??
      (rideSlot.ride_details ?? [])[0])
    : undefined;
  const ridePickupAddress = rideLeg?.pickup_address?.formatted_address;
  const rideDropoffAddress = rideLeg?.dropoff_address?.formatted_address;
  /* Multi-véhicules : une réservation = N véhicules (un créneau par véhicule ET par trajet).
     À un seul véhicule la liste tient une entrée et l'affichage reste strictement l'actuel. */
  const rideVehicles = isRide && booking ? getRideVehicles(booking) : [];
  const hasSeveralRideVehicles = rideVehicles.length > 1;
  const rideTotalPassengers = getRideTotalPassengers(rideVehicles);
  const canCancel = booking?.can_cancel === true;
  const canReschedule = booking?.can_reschedule === true;
  // Le pro valide chaque réservation : déplacer = envoyer une demande (textes adaptés).
  const isRescheduleRequest = booking?.reschedule_requires_pro_confirmation === true;
  const isDeferredCollectionPayment = booking ? isDeferredCollection(booking) : false;
  const hasSettledOnlinePayment =
    booking?.payment_status === ProPaymentStatus.PAYED ||
    booking?.payment_status === ProPaymentStatus.PARTIALLY_REFUNDED ||
    booking?.payment_status === ProPaymentStatus.MANUALLY_PAID;
  const hasOnlinePayment = hasSettledOnlinePayment && (booking?.payment_infos.amount_to_pay_today_raw ?? 0) > 0;
  const remainingOnSiteAmount =
    !hasSettledOnlinePayment && (booking?.payment_infos.amount_to_pay_today_raw ?? 0) > 0
      ? booking?.payment_infos.amount_to_pay_today
      : booking?.payment_infos.remaining_amount_on_site;
  const hasQuoteOrFromPricing = bookingSlots.some((slot): boolean =>
    (slot.prestations ?? []).some(
      (prestation): boolean => prestation.price_type === PriceType.QUOTE || prestation.price_type === PriceType.FROM,
    ),
  );
  // Au moins une prestation « aucun prix » (NONE) : le montant sera fixé sur place → on remplace
  // les montants à 0 (« 0,00 € ») par « À déterminer » pour lever l'ambiguïté.
  const hasNoPrice = bookingSlots.some((slot): boolean =>
    (slot.prestations ?? []).some((prestation): boolean => prestation.price_type === PriceType.NONE),
  );
  // Montant « plancher » : QUOTE/FROM (sur devis / à partir de) OU NONE mêlé à des prestas chiffrées.
  // Montant connu → « À partir de X » ; pur NONE sans montant ferme → « À déterminer ».
  const hasLowerBoundPricing = hasQuoteOrFromPricing || hasNoPrice;
  const flexibleAmountLabels = { fromPrefix: t('PRICE_FROM_PREFIX'), onQuote: t('PRICE_ON_QUOTE') };
  const totalAmountDisplay =
    hasNoPrice && !hasNonZeroFormattedAmount(booking?.payment_infos.total_amount)
      ? t('PRICE_TO_DETERMINE')
      : formatFlexibleAmount(booking?.payment_infos.total_amount, hasLowerBoundPricing, flexibleAmountLabels);
  const remainingOnSiteDisplay =
    hasNoPrice && !hasNonZeroFormattedAmount(remainingOnSiteAmount)
      ? t('PRICE_TO_DETERMINE')
      : formatFlexibleAmount(remainingOnSiteAmount, hasLowerBoundPricing, flexibleAmountLabels);
  /* Récap aligné sur celui du tunnel (bloc `amounts`) : remise sur sa ligne, total après remise avec le
     prix normal barré, bons ; absent d'un backend antérieur, qui garde les lignes code / réduction. */
  const amounts = booking?.amounts;
  const netTotalDisplay =
    hasNoPrice && !hasNonZeroFormattedAmount(amounts?.net_total)
      ? t('PRICE_TO_DETERMINE')
      : formatFlexibleAmount(amounts?.net_total, hasLowerBoundPricing, flexibleAmountLabels);
  const struckTotal = amounts && amounts.net_total_raw < amounts.gross_total_raw ? amounts.gross_total : undefined;
  const isCancelledBooking =
    booking?.reservation_status === ReservationStatus.CANCELLED_BY_PRO ||
    booking?.reservation_status === ReservationStatus.CANCELLED_BY_CUSTOMER;
  const isAwaitingConfirmation = booking?.reservation_status === ReservationStatus.WAITING_CONFIRMATION;
  const cancellationReasonLabel = booking?.cancellation_reason?.trim() || t('CANCELLATION_REASON_EMPTY');
  // Frais d'annulation tardive réellement prélevés : réponse d'annulation en priorité, sinon
  // (rechargement de page) déduits d'une résa annulée dont le paiement différé est passé PAYED.
  const penaltyChargedAmount =
    cancelResult?.late_cancel_penalty_charge_status === LateCancelPenaltyChargeStatus.CHARGED
      ? cancelResult.late_cancel_penalty_amount
      : booking?.reservation_status === ReservationStatus.CANCELLED_BY_CUSTOMER &&
          isDeferredCollectionPayment &&
          booking?.payment_status === ProPaymentStatus.PAYED
        ? booking?.booking_cancellation_refund_info?.late_cancel_penalty_amount
        : undefined;
  const penaltyChargeFailed = cancelResult?.late_cancel_penalty_charge_status === LateCancelPenaltyChargeStatus.FAILED;
  const cancelledBookingNoticeKey =
    booking?.reservation_status === ReservationStatus.CANCELLED_BY_PRO
      ? 'CANCELLED_BY_PRO_NOTICE'
      : 'CANCELLED_BY_CUSTOMER_NOTICE';
  const rescheduleSlotPickerLabels = useMemo(
    (): {
      previousWeek: string;
      nextWeek: string;
      morning: string;
      afternoon: string;
      evening: string;
      noSlots: string;
      noSlotsForDay: string;
      loading: string;
      placesOne: string;
      placesOther: string;
      full: string;
    } => ({
      previousWeek: t('PREVIOUS_WEEK'),
      nextWeek: t('NEXT_WEEK'),
      morning: t('MORNING_LABEL'),
      afternoon: t('AFTERNOON_LABEL'),
      evening: t('EVENING_LABEL'),
      noSlots: t('NO_SLOTS'),
      noSlotsForDay: t('NO_SLOTS_FOR_DAY'),
      loading: translate('fr-FR', 'BOOKING_TUNNEL_NAV', 'SLOTS_LOADING') ?? '',
      /* Compteur de places des créneaux séance (le reschedule n'envoie pas le drapeau : seuls des
         créneaux réservables arrivent, le compteur est informatif — jamais de grisage ici). */
      placesOne: t('SLOT_PLACES_ONE'),
      placesOther: t('SLOT_PLACES_OTHER'),
      full: t('SLOT_FULL'),
    }),
    [],
  );

  const closeModal = (): void => {
    if (isCancelling || isRescheduling) return;
    setActiveModal(null);
  };

  useEffect((): (() => void) => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setMemberImagePreview(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return (): void => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const renderMemberRow = (member: BookingDisplayMember, key: string): React.JSX.Element => {
    const avatar = (
      <TeamMemberAvatar
        member={{
          firstname: member.name,
          lastname: undefined,
          picture: member.pictureUrl ? [{ preview_url: member.pictureUrl }] : undefined,
        }}
        size="sm"
      />
    );

    return (
      <div key={key} className="manage-booking-page__member-row">
        {member.pictureUrl ? (
          <button
            type="button"
            className="manage-booking-page__member-avatar-button"
            onClick={(): void => setMemberImagePreview({ src: member.pictureUrl ?? '', alt: member.name })}
            aria-label={`${t('TEAM_MEMBER_LABEL')} ${member.name}`}
          >
            {avatar}
          </button>
        ) : (
          avatar
        )}
        <span className="manage-booking-page__member-name">{member.name}</span>
      </div>
    );
  };

  const renderActionModal = (): React.JSX.Element | null => {
    if (activeModal === null) return null;

    const isCancelModal = activeModal === 'cancel';
    const isReschedulePickerModal = activeModal === 'reschedule-picker';
    const modalTitleId = isCancelModal
      ? 'cancel-booking-modal-title'
      : isReschedulePickerModal
        ? 'reschedule-picker-modal-title'
        : 'reschedule-booking-modal-title';
    const modalDescriptionId = isCancelModal
      ? 'cancel-booking-modal-description'
      : isReschedulePickerModal
        ? 'reschedule-picker-modal-description'
        : 'reschedule-booking-modal-description';
    const reasonInputId = isCancelModal ? 'cancellation-reason' : 'reschedule-reason';
    const isModalLoading = isCancelModal ? isCancelling : isRescheduling;
    const isRescheduleRequestConfirm = !isCancelModal && !isReschedulePickerModal && isRescheduleRequest;
    const confirmButtonClassName = isCancelModal
      ? 'manage-booking-page__button manage-booking-page__button--danger'
      : 'manage-booking-page__button';
    const confirmButtonLabel = isModalLoading
      ? t('LOADING')
      : t(
          isCancelModal
            ? 'CANCEL_CONFIRM_BUTTON'
            : isRescheduleRequestConfirm
              ? 'RESCHEDULE_REQUEST_CONFIRM_BUTTON'
              : 'RESCHEDULE_CONFIRM_BUTTON',
        );
    const closeButtonLabel = t(isCancelModal ? 'CANCEL_CONFIRM_BACK' : 'RESCHEDULE_CONFIRM_BACK');

    return (
      <div className="manage-booking-page__modal-backdrop" onClick={closeModal}>
        <section
          className={
            isReschedulePickerModal
              ? 'manage-booking-page__modal manage-booking-page__modal--wide'
              : 'manage-booking-page__modal'
          }
          role="dialog"
          aria-modal="true"
          aria-labelledby={modalTitleId}
          aria-describedby={modalDescriptionId}
          onClick={(event): void => event.stopPropagation()}
        >
          <h2 id={modalTitleId}>
            {t(
              isCancelModal
                ? 'CANCEL_CONFIRM_TITLE'
                : isReschedulePickerModal
                  ? 'RESCHEDULE_PICKER_TITLE'
                  : isRescheduleRequestConfirm
                    ? 'RESCHEDULE_REQUEST_CONFIRM_TITLE'
                    : 'RESCHEDULE_CONFIRM_TITLE',
            )}
          </h2>
          <p id={modalDescriptionId}>
            {t(
              isCancelModal
                ? refundDetails?.deferredPenalty?.willBeCharged
                  ? 'CANCEL_CONFIRM_TEXT_PENALTY'
                  : 'CANCEL_CONFIRM_TEXT'
                : isReschedulePickerModal
                  ? 'RESCHEDULE_PICKER_TEXT'
                  : isRescheduleRequestConfirm
                    ? 'RESCHEDULE_REQUEST_CONFIRM_TEXT'
                    : 'RESCHEDULE_CONFIRM_TEXT',
            ).replace('{pro}', booking?.professional_name ?? '')}
          </p>
          {/* Le bouton d'annulation porte TOUJOURS sur la réservation entière : sur une course à
              plusieurs véhicules, on le dit explicitement avant de confirmer. */}
          {isCancelModal && hasSeveralRideVehicles ? (
            <p>{t('RIDE_VEHICLES_CANCEL_NOTICE').replace('{count}', String(rideVehicles.length))}</p>
          ) : null}
          {isCancelModal && refundDetails?.deferredPenalty?.isPastDeadline ? (
            <div className="manage-booking-page__modal-refund manage-booking-page__modal-refund--warning">
              <span>{t('LATE_CANCEL_PENALTY_LABEL')}</span>
              <strong>
                {t(
                  refundDetails.deferredPenalty.isBankImprint
                    ? 'LATE_CANCEL_PENALTY_IMPRINT_NOTICE'
                    : 'LATE_CANCEL_PENALTY_CARD_NOTICE',
                ).replace('{amount}', refundDetails.deferredPenalty.amount)}
              </strong>
            </div>
          ) : null}
          {isCancelModal ? (
            hasRefundDetails ? (
              <div className="manage-booking-page__modal-refund">
                <span>{t('REFUND_AMOUNT_LABEL')}</span>
                <div className="manage-booking-page__modal-refund-values">
                  {refundDetails?.paymentRefundAmount ? (
                    <div>
                      <span>{t('REFUND_ONLINE_PAYMENT_LABEL')}</span>
                      <strong>{refundDetails.paymentRefundAmount}</strong>
                    </div>
                  ) : null}
                  {refundDetails?.voucherRefund ? (
                    <div>
                      <span>{t('REFUND_VOUCHER_LABEL')}</span>
                      <strong>
                        {refundDetails.voucherRefund.code} - {refundDetails.voucherRefund.amount}
                      </strong>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : refundDetails?.deferredPenalty?.isPastDeadline ? null : (
              <div className="manage-booking-page__modal-refund">
                <span>{t(refundDetails?.isDeferredCollection ? 'CANCEL_CHARGE_LABEL' : 'REFUND_AMOUNT_LABEL')}</span>
                <strong>
                  {t(refundDetails?.isDeferredCollection ? 'DEFERRED_CANCEL_NO_CHARGE_LABEL' : 'NO_REFUND_LABEL')}
                </strong>
              </div>
            )
          ) : null}
          {isReschedulePickerModal && primarySlot ? (
            <div className="manage-booking-page__modal-selected-slot">
              <span>{t('RESCHEDULE_CURRENT_SLOT_LABEL')}</span>
              <strong>{primarySlot.start_formatted}</strong>
            </div>
          ) : null}
          {isReschedulePickerModal ? (
            <>
              {/* Remise tenue au créneau (lot 4) : seuls les créneaux qui la gardent sont proposés, et on le dit. */}
              {booking?.reschedule_promotion_name ? (
                <p className="manage-booking-page__promotion-notice">
                  {t('RESCHEDULE_PROMOTION_NOTICE').replace('{name}', booking.reschedule_promotion_name)}
                </p>
              ) : null}
              <SlotWeekPicker
                slots={slots}
                selectedSlot={selectedSlot ?? undefined}
                selectedDate={selectedSlotDate}
                weekStart={slotWeekStart}
                labels={rescheduleSlotPickerLabels}
                locale="fr-FR"
                loading={isLoadingSlots}
                variant="manage"
                onSelectDate={(date): void => {
                  setSelectedSlotDate(date);
                  setSelectedSlot(null);
                }}
                onSelectSlot={(slot): void => {
                  setSelectedSlot(slot);
                }}
                onWeekChange={handleRescheduleWeekChange}
              />
              {actionError ? <p className="manage-booking-page__error">{actionError}</p> : null}
            </>
          ) : null}
          {!isCancelModal && !isReschedulePickerModal && selectedSlot ? (
            <div className="manage-booking-page__modal-selected-slot">
              <span>{t('SELECTED_SLOT_LABEL')}</span>
              <strong>{getBookableSlotLabel(selectedSlot)}</strong>
            </div>
          ) : null}
          {!isReschedulePickerModal ? (
            <>
              <label htmlFor={reasonInputId}>
                {t(isCancelModal ? 'CANCEL_REASON_LABEL' : 'RESCHEDULE_REASON_LABEL')}
              </label>
              <textarea
                id={reasonInputId}
                maxLength={1000}
                value={isCancelModal ? cancellationReason : rescheduleReason}
                placeholder={t(isCancelModal ? 'CANCEL_REASON_PLACEHOLDER' : 'RESCHEDULE_REASON_PLACEHOLDER')}
                onChange={(event): void => {
                  if (isCancelModal) {
                    setCancellationReason(event.target.value);
                  } else {
                    setRescheduleReason(event.target.value);
                  }
                }}
              />
            </>
          ) : null}
          <div className="manage-booking-page__modal-actions">
            {isReschedulePickerModal ? (
              <button
                type="button"
                className="manage-booking-page__button"
                disabled={!selectedSlot || isLoadingSlots}
                onClick={(): void => setActiveModal('reschedule-confirm')}
              >
                {t('RESCHEDULE_SELECTED_SLOT_BUTTON')}
              </button>
            ) : (
              <button
                type="button"
                className={confirmButtonClassName}
                disabled={isModalLoading}
                onClick={(): void => {
                  if (isCancelModal) {
                    void handleCancel();
                  } else {
                    void handleReschedule();
                  }
                }}
              >
                {confirmButtonLabel}
              </button>
            )}
            <button
              type="button"
              className="manage-booking-page__button manage-booking-page__button--secondary"
              disabled={isModalLoading || (isReschedulePickerModal && isLoadingSlots)}
              onClick={(): void => {
                if (activeModal === 'reschedule-confirm') {
                  setActiveModal('reschedule-picker');
                } else {
                  closeModal();
                }
              }}
            >
              {isReschedulePickerModal ? t('CANCEL_CONFIRM_BACK') : closeButtonLabel}
            </button>
          </div>
        </section>
      </div>
    );
  };

  return (
    <section className={rootClassName}>
      {showIntro ? (
        <div className="manage-booking-page__intro">
          <div>
            <h1>{t('MANAGE_TITLE')}</h1>
            <p>{t('MANAGE_SUBTITLE')}</p>
          </div>
        </div>
      ) : null}

      {isLoadingBooking ? <p className="manage-booking-page__status">{t('LOADING')}</p> : null}

      {bookingError ? (
        <section className="manage-booking-page__panel">
          <p className="manage-booking-page__error">{bookingError}</p>
          <button type="button" className="manage-booking-page__button" onClick={(): void => void loadBooking()}>
            {t('RETRY')}
          </button>
        </section>
      ) : null}

      {booking ? (
        <>
          {isAwaitingConfirmation ? (
            <aside className="manage-booking-page__awaiting-notice" aria-label={t('AWAITING_CONFIRMATION_TITLE')}>
              <div className="manage-booking-page__awaiting-notice-header">
                <FiClock aria-hidden="true" />
                <div>
                  <span>{t('AWAITING_CONFIRMATION_TITLE')}</span>
                  <strong>{t('AWAITING_CONFIRMATION_NOTICE')}</strong>
                </div>
              </div>
            </aside>
          ) : null}

          <section className="manage-booking-page__summary-card">
            {primarySlot ? (
              <header className="manage-booking-page__summary-header">
                <span>{t('DATE_TIME_LABEL')}</span>
                <strong>
                  <FiCalendar aria-hidden="true" />
                  {primarySlot.start_formatted}
                </strong>
              </header>
            ) : null}

            <div className="manage-booking-page__detail-grid">
              {isRide && (ridePickupAddress || rideDropoffAddress) ? (
                <DetailItem icon={FiMapPin} label={t('RIDE_RECAP_TITLE')}>
                  {ridePickupAddress ? (
                    <a
                      href={buildMapSearchUrl(ridePickupAddress)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="manage-booking-page__detail-link"
                    >
                      <span>
                        {t('RIDE_PICKUP')} : {ridePickupAddress}
                      </span>
                      <FiMapPin className="manage-booking-page__detail-link-icon" aria-hidden="true" />
                    </a>
                  ) : null}
                  {rideDropoffAddress ? (
                    <a
                      href={buildMapSearchUrl(rideDropoffAddress)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="manage-booking-page__detail-link"
                    >
                      <span>
                        {t('RIDE_DROPOFF')} : {rideDropoffAddress}
                      </span>
                      <FiMapPin className="manage-booking-page__detail-link-icon" aria-hidden="true" />
                    </a>
                  ) : null}
                </DetailItem>
              ) : null}
              {!isRide && primarySlot ? (
                <DetailItem icon={FiMapPin} label={t('PLACE_LABEL')}>
                  {primarySlot.pro_place_label ? <span>{primarySlot.pro_place_label}</span> : null}
                  {placeAddress ? (
                    <a
                      href={buildMapSearchUrl(placeAddress)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="manage-booking-page__detail-link"
                    >
                      <span>{placeAddress}</span>
                      <FiMapPin className="manage-booking-page__detail-link-icon" aria-hidden="true" />
                    </a>
                  ) : null}
                </DetailItem>
              ) : null}

              <DetailItem icon={FiUser} label={t('PROFESSIONAL_LABEL')}>
                <span>{booking.professional_name}</span>
                <a href={`mailto:${booking.professional_email_contact}`} className="manage-booking-page__detail-link">
                  <span>{booking.professional_email_contact}</span>
                  <FiMail className="manage-booking-page__detail-link-icon" aria-hidden="true" />
                </a>
                {booking.professional_phone_number ? (
                  <a href={`tel:${booking.professional_phone_number}`} className="manage-booking-page__detail-link">
                    <span>{booking.professional_phone_number}</span>
                    <FiPhone className="manage-booking-page__detail-link-icon" aria-hidden="true" />
                  </a>
                ) : null}
              </DetailItem>

              <DetailItem icon={FiFileText} label={t('CUSTOMER_LABEL')}>
                <span>{getCustomerName(booking)}</span>
                <span>{booking.customer_email}</span>
              </DetailItem>

              {/* Réservation à plusieurs véhicules : classe, passagers, bagages, chauffeur et prix
                  de CHAQUE véhicule. Masqué sur une course mono-véhicule. */}
              {hasSeveralRideVehicles ? (
                <DetailItem icon={FiTruck} label={t('RIDE_VEHICLES').replace('{count}', String(rideVehicles.length))}>
                  {rideVehicles.map(
                    (vehicle): React.JSX.Element => (
                      <span key={vehicle.vehicleIndex}>{formatRideVehicleLine(vehicle)}</span>
                    ),
                  )}
                </DetailItem>
              ) : null}

              {isRide ? (
                <DetailItem icon={FiFileText} label={t('RIDE_TRIP_LABEL')}>
                  {!hasSeveralRideVehicles && rideSlot?.vehicle_name ? (
                    <span>
                      {t('RIDE_VEHICLE')} : {rideSlot.vehicle_name}
                    </span>
                  ) : null}
                  {hasSeveralRideVehicles ? (
                    rideTotalPassengers != null ? (
                      <span>
                        {t('RIDE_PASSENGERS_TOTAL')} : {rideTotalPassengers}
                      </span>
                    ) : null
                  ) : rideLeg?.passenger_count != null ? (
                    <span>
                      {t('RIDE_PASSENGERS')} : {rideLeg.passenger_count}
                    </span>
                  ) : null}
                  {!hasSeveralRideVehicles
                    ? formatRideLuggageLines(
                        rideLeg?.luggage_count,
                        rideLeg?.small_luggage_count,
                        rideLeg?.large_luggage_count,
                      ).map((line): React.JSX.Element => <span key={line}>{line}</span>)
                    : null}
                  {rideLeg?.distance_km != null ? (
                    <span>
                      {t('RIDE_DISTANCE')} : {rideLeg.distance_km} km
                    </span>
                  ) : null}
                  {rideLeg?.estimated_duration_min != null ? (
                    <span>
                      {t('RIDE_DURATION')} : {formatDurationFromMinutes(rideLeg.estimated_duration_min)}
                    </span>
                  ) : null}
                </DetailItem>
              ) : (
                <DetailItem icon={FiFileText} label={t('SERVICES_LABEL')}>
                  {serviceSummaries.map(
                    (serviceSummary, index): React.JSX.Element => (
                      <span key={`${serviceSummary}-${index}`}>{serviceSummary}</span>
                    ),
                  )}
                </DetailItem>
              )}

              {memberDetails.length > 0 ? (
                <DetailItem
                  icon={FiUser}
                  label={t(memberDetails.length > 1 ? 'TEAM_MEMBERS_LABEL' : 'TEAM_MEMBER_LABEL')}
                >
                  <div className="manage-booking-page__member-list">
                    {memberDetails.map((member): React.JSX.Element => renderMemberRow(member, member.key))}
                  </div>
                </DetailItem>
              ) : null}

              {booking.note ? (
                <DetailItem icon={FiFileText} label={t('NOTE_LABEL')}>
                  <span>{booking.note}</span>
                </DetailItem>
              ) : null}
            </div>

            {bookingSlots.length > 0 ? (
              <section className="manage-booking-page__appointment-list" aria-label={t('APPOINTMENT_SECTION')}>
                {bookingSlots.map((slot, index): React.JSX.Element => {
                  const slotMembers = getSlotMembers(slot);
                  return (
                    <article
                      key={`${slot.start_formatted ?? 'slot'}-${index}`}
                      className="manage-booking-page__appointment"
                    >
                      <span>{t('DATE_TIME_LABEL')}</span>
                      <strong>{getSlotDateTime(slot)}</strong>
                      {slotMembers.length > 0 ? (
                        <div className="manage-booking-page__slot-members">
                          {slotMembers.map(
                            (member): React.JSX.Element => renderMemberRow(member, `${member.key}-slot-${index}`),
                          )}
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </section>
            ) : null}

            {Array.isArray(booking.instructions) && booking.instructions.length > 0 ? (
              <section className="manage-booking-page__instructions" aria-label={t('INSTRUCTIONS_HEADING')}>
                <header className="manage-booking-page__instructions-header">
                  <FiFileText aria-hidden="true" />
                  <h2>{t('INSTRUCTIONS_HEADING')}</h2>
                </header>
                {booking.instructions.map(
                  (section): React.JSX.Element => (
                    <article key={section.booking_instruction_id} className="manage-booking-page__instructions-section">
                      {section.title ? (
                        <h3 className="manage-booking-page__instructions-title">{section.title}</h3>
                      ) : null}
                      <div
                        className="manage-booking-page__instructions-body"
                        dangerouslySetInnerHTML={{ __html: section.content_html }}
                      />
                      {Array.isArray(section.attachments) && section.attachments.length > 0 ? (
                        <div className="manage-booking-page__instructions-attachments">
                          <span className="manage-booking-page__instructions-attachments-label">
                            {t('INSTRUCTIONS_ATTACHMENTS_LABEL')}
                          </span>
                          <ul>
                            {section.attachments.map(
                              (attachment): React.JSX.Element => (
                                <li key={attachment.attachment_token}>
                                  <a href={attachment.download_url} target="_blank" rel="noopener noreferrer">
                                    {attachment.file_name}
                                  </a>
                                </li>
                              ),
                            )}
                          </ul>
                        </div>
                      ) : null}
                    </article>
                  ),
                )}
              </section>
            ) : null}

            <div className="manage-booking-page__payment-summary">
              {amounts ? (
                <>
                  {(amounts.discount_lines ?? []).map(
                    (line): ReactNode => (
                      <div
                        key={`discount-${line.kind ?? ''}-${line.code ?? line.label}`}
                        className="manage-booking-page__payment-row--discount"
                      >
                        <span>
                          {discountLineLabel(line, {
                            promo: codesLabel('DISCOUNT_LINE'),
                            manual: codesLabel('MANUAL_DISCOUNT_LINE'),
                          })}
                        </span>
                        <strong>{line.amount}</strong>
                      </div>
                    ),
                  )}
                  <div>
                    <span>{t('TOTAL_AFTER_DISCOUNT_LABEL')}</span>
                    <strong>
                      {struckTotal ? <s className="manage-booking-page__total-struck">{struckTotal}</s> : null}
                      {netTotalDisplay}
                    </strong>
                  </div>
                  {(amounts.voucher_lines ?? []).map(
                    (line): ReactNode => (
                      <div key={`voucher-${line.code}`}>
                        <span>
                          {voucherLineLabel(line, {
                            amount: codesLabel('VOUCHER_LINE_AMOUNT'),
                            pack: codesLabel('VOUCHER_LINE_PACK'),
                            prestation: codesLabel('VOUCHER_LINE_PRESTATION'),
                          })}
                        </span>
                        <strong>{line.amount}</strong>
                      </div>
                    ),
                  )}
                </>
              ) : (
                <>
                  <div>
                    <span>{t('TOTAL_AMOUNT_LABEL')}</span>
                    <strong>{totalAmountDisplay}</strong>
                  </div>
                  {booking.voucher_code ? (
                    <div>
                      <span>{t('VOUCHER_CODE_LABEL')}</span>
                      <strong>{booking.voucher_code}</strong>
                    </div>
                  ) : null}
                  {booking.voucher_discount ? (
                    <div>
                      <span>{t('VOUCHER_DISCOUNT_LABEL')}</span>
                      <strong>-{booking.voucher_discount}</strong>
                    </div>
                  ) : null}
                  {booking.promo_code ? (
                    <div>
                      <span>{t('PROMO_CODE_LABEL')}</span>
                      <strong>{booking.promo_code}</strong>
                    </div>
                  ) : null}
                  {booking.promo_discount ? (
                    <div>
                      <span>{t('PROMO_DISCOUNT_LABEL')}</span>
                      <strong>-{booking.promo_discount}</strong>
                    </div>
                  ) : null}
                </>
              )}
              {isDeferredCollectionPayment || hasSettledOnlinePayment ? (
                <div>
                  <span>{t(isDeferredCollectionPayment ? 'CHARGED_AT_BOOKING_LABEL' : 'PAID_ONLINE_LABEL')}</span>
                  <strong>{booking.payment_infos.amount_to_pay_today}</strong>
                </div>
              ) : null}
              <div className={hasOnlinePayment ? undefined : 'manage-booking-page__payment-row--highlight'}>
                <span>{t(isDeferredCollectionPayment ? 'AMOUNT_DUE_LATER_LABEL' : 'REMAINING_ON_SITE_LABEL')}</span>
                <strong>{remainingOnSiteDisplay}</strong>
              </div>
            </div>

            {isCancelledBooking ? (
              <aside className="manage-booking-page__cancellation-details" aria-label={t('CANCELLATION_DETAILS_TITLE')}>
                <div className="manage-booking-page__cancellation-details-header">
                  <FiAlertCircle aria-hidden="true" />
                  <div>
                    <span>{t('CANCELLATION_DETAILS_TITLE')}</span>
                    <strong>{t(cancelledBookingNoticeKey)}</strong>
                  </div>
                </div>
                <div className="manage-booking-page__cancellation-details-grid">
                  {booking.cancelled_at_formatted ? (
                    <div>
                      <span>{t('CANCELLATION_DATE_LABEL')}</span>
                      <strong>{booking.cancelled_at_formatted}</strong>
                    </div>
                  ) : null}
                  <div>
                    <span>{t('CANCEL_REASON_LABEL')}</span>
                    <strong>{cancellationReasonLabel}</strong>
                  </div>
                  {booking.refunded_amount ? (
                    <div>
                      <span>{t('REFUNDED_AMOUNT_LABEL')}</span>
                      <strong>{booking.refunded_amount}</strong>
                    </div>
                  ) : null}
                  {penaltyChargedAmount ? (
                    <div>
                      <span>{t('LATE_CANCEL_PENALTY_CHARGED_LABEL')}</span>
                      <strong>{penaltyChargedAmount}</strong>
                    </div>
                  ) : null}
                </div>
              </aside>
            ) : null}

            {!isCancelledBooking && booking.cancellation_deadline_formatted ? (
              <aside className="manage-booking-page__deadline-notice">
                <span>{t('CANCELLATION_DEADLINE_LABEL')}</span>
                <strong>{booking.cancellation_deadline_formatted}</strong>
                {refundDetails?.deferredPenalty ? (
                  <span className="manage-booking-page__deadline-penalty-hint">
                    {t(
                      refundDetails.deferredPenalty.isBankImprint
                        ? 'LATE_CANCEL_PENALTY_IMPRINT_DEADLINE_HINT'
                        : 'LATE_CANCEL_PENALTY_DEADLINE_HINT',
                    ).replace('{amount}', refundDetails.deferredPenalty.amount)}
                  </span>
                ) : null}
              </aside>
            ) : null}

            {!isCancelledBooking ? (
              <footer className="manage-booking-page__action-row">
                {canReschedule ? (
                  <button
                    type="button"
                    className="manage-booking-page__button manage-booking-page__button--secondary"
                    disabled={isLoadingSlots}
                    onClick={handleOpenReschedulePanel}
                  >
                    <FiClock aria-hidden="true" />
                    {isLoadingSlots ? t('LOADING') : t('RESCHEDULE_OPEN_BUTTON')}
                  </button>
                ) : null}

                {canCancel ? (
                  <button
                    type="button"
                    className="manage-booking-page__button manage-booking-page__button--danger"
                    disabled={isCancelling}
                    onClick={(): void => setActiveModal('cancel')}
                  >
                    <FiAlertCircle aria-hidden="true" />
                    {isCancelling ? t('LOADING') : t('CANCEL_BUTTON')}
                  </button>
                ) : (
                  <p className="manage-booking-page__status">{t('CANCEL_UNAVAILABLE')}</p>
                )}
              </footer>
            ) : null}
          </section>
        </>
      ) : null}

      {actionMessage ? <p className="manage-booking-page__success">{actionMessage}</p> : null}
      {penaltyChargeFailed ? (
        <p className="manage-booking-page__error">{t('LATE_CANCEL_PENALTY_CHARGE_FAILED_NOTICE')}</p>
      ) : null}
      {actionError ? <p className="manage-booking-page__error">{actionError}</p> : null}
      {renderActionModal()}
      {memberImagePreview ? (
        <div className="manage-booking-page__image-lightbox" onClick={(): void => setMemberImagePreview(null)}>
          <div
            className="manage-booking-page__image-lightbox-content"
            onClick={(event): void => event.stopPropagation()}
          >
            <button
              type="button"
              className="manage-booking-page__image-lightbox-close"
              onClick={(): void => setMemberImagePreview(null)}
              aria-label={t('CANCEL_CONFIRM_BACK')}
            >
              <FiX aria-hidden="true" />
            </button>
            <img
              src={memberImagePreview.src}
              alt={memberImagePreview.alt}
              className="manage-booking-page__image-lightbox-image"
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}
